import type { ShareWorkspace } from "@/components/acl/share-workspaces";
import { GroupDisplayName } from "@/components/display/GroupDisplayName";
import { UserDisplayName } from "@/components/display/UserDisplayName";
import { cn } from "@/lib/utils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronRight } from "@nnr/fontawesome-sharp-light/icons/sharp/light";
import { useState, type ReactNode } from "react";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../ui/collapsible";
import { SearchInput } from "../ui/input";

type ShareWorkspacesSectionProps = {
  totalWorkspaces: number;
  selected: ShareWorkspace[];
  recent: ShareWorkspace[];
  unselected: ShareWorkspace[];
  searchQuery: string;
  onSearchQueryChange: (query: string) => void;
  isLoading?: boolean;
  onEnable: (groupUuid: string) => void;
  onDisable: (groupUuid: string) => void;
  onSelectAll: () => void;
  onUnselectAll: () => void;
  renderTrailing?: (groupUuid: string) => ReactNode;
};

const ShareWorkspaceName = ({ workspace }: { workspace: ShareWorkspace }) => {
  if (workspace.kind === "person") {
    return (
      <UserDisplayName
        user={{ name: workspace.name, email: workspace.email }}
      />
    );
  }

  return <GroupDisplayName group={{ name: workspace.name }} />;
};

const ShareWorkspaceRow = ({
  workspace,
  checked,
  isLoading,
  onEnable,
  onDisable,
  trailing,
}: {
  workspace: ShareWorkspace;
  checked: boolean;
  isLoading?: boolean;
  onEnable: (groupUuid: string) => void;
  onDisable: (groupUuid: string) => void;
  trailing?: ReactNode;
}) => (
  <div className="flex items-center gap-2">
    <Checkbox
      id={`share-workspace-${workspace.groupUuid}`}
      disabled={isLoading}
      checked={checked}
      onCheckedChange={(next) =>
        next ? onEnable(workspace.groupUuid) : onDisable(workspace.groupUuid)
      }
    />
    <label
      htmlFor={`share-workspace-${workspace.groupUuid}`}
      className="flex-1 cursor-pointer"
    >
      <ShareWorkspaceName workspace={workspace} />
    </label>
    {trailing}
  </div>
);

const WorkspaceGroup = ({
  title,
  workspaces,
  checked,
  isLoading,
  onEnable,
  onDisable,
  renderTrailing,
}: {
  title: string;
  workspaces: ShareWorkspace[];
  checked: boolean;
  isLoading?: boolean;
  onEnable: (groupUuid: string) => void;
  onDisable: (groupUuid: string) => void;
  renderTrailing?: (groupUuid: string) => ReactNode;
}) => {
  const [open, setOpen] = useState(true);

  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      className="flex flex-col gap-2"
    >
      <CollapsibleTrigger className="text-muted-foreground flex cursor-pointer items-center gap-2 text-xs font-medium tracking-wide uppercase">
        <FontAwesomeIcon
          icon={faChevronRight}
          className={cn("transition-transform", { "rotate-90": open })}
        />
        <span>{workspaces.length}</span>
        {title}
      </CollapsibleTrigger>
      <CollapsibleContent className="flex flex-col gap-2">
        {workspaces.map((workspace) => (
          <ShareWorkspaceRow
            key={workspace.groupUuid}
            workspace={workspace}
            checked={checked}
            isLoading={isLoading}
            onEnable={onEnable}
            onDisable={onDisable}
            trailing={renderTrailing?.(workspace.groupUuid)}
          />
        ))}
      </CollapsibleContent>
    </Collapsible>
  );
};

export const ShareWorkspacesSection = ({
  selected,
  recent,
  unselected,
  searchQuery,
  totalWorkspaces,
  onSearchQueryChange,
  isLoading,
  onEnable,
  onDisable,
  onSelectAll,
  onUnselectAll,
  renderTrailing,
}: ShareWorkspacesSectionProps) => (
  <div className="flex flex-col gap-3">
    <div className="flex w-full flex-row items-center gap-2">
      <Button
        variant="outline"
        size="sm"
        onClick={onSelectAll}
        disabled={isLoading || recent.length + unselected.length === 0}
      >
        Select All
      </Button>
      <Button
        variant="outline"
        size="sm"
        onClick={onUnselectAll}
        disabled={isLoading || selected.length === 0}
      >
        Unselect All
      </Button>
      <div className="flex-1">
        <SearchInput
          placeholder="Search by name or email"
          value={searchQuery}
          onChange={(event) => onSearchQueryChange(event.target.value)}
          disabled={isLoading}
        />
      </div>
    </div>
    <div className="text-muted-foreground text-sm">
      {searchQuery.length === 0
        ? `${totalWorkspaces} workspaces`
        : `Showing ${selected.length + recent.length + unselected.length} of ${totalWorkspaces} workspaces`}
    </div>
    <WorkspaceGroup
      title="Selected"
      workspaces={selected}
      checked
      isLoading={isLoading}
      onEnable={onEnable}
      onDisable={onDisable}
      renderTrailing={renderTrailing}
    />
    <WorkspaceGroup
      title="Recent"
      workspaces={recent}
      checked={false}
      isLoading={isLoading}
      onEnable={onEnable}
      onDisable={onDisable}
      renderTrailing={renderTrailing}
    />
    <WorkspaceGroup
      title="Unselected"
      workspaces={unselected}
      checked={false}
      isLoading={isLoading}
      onEnable={onEnable}
      onDisable={onDisable}
      renderTrailing={renderTrailing}
    />
  </div>
);
